import React, {useMemo} from 'react'
import styled from 'styled-components'
import {AVATAR_SIZE_CHAT} from 'consts'
import {
  FirebaseMessageRoomItem,
  FirebaseMessageRoomMemberItem,
  FirebaseMessageRoomType,
  MessageMode,
  ObjectState,
} from 'types'
import {
  getDefaultAvatar,
  useFirebaseUser,
  useHistory,
  useMessageContent,
} from 'utils'
import {Avatar, Icon, IconVerifiedBadge, Paragraph} from 'common/components'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'

interface ContainerProps {
  isSelected: boolean
}
const StyledContainer = styled.div<ContainerProps>`
  flex-direction: row;
  display: flex;
  align-items: center;
  padding: ${convertUnit(16)} ${convertUnit(20)};
  background-color: ${({isSelected, theme}) =>
    theme[isSelected ? 'primary_1' : 'white_1']};
  cursor: pointer;
`
const StyledHeader = styled.div`
  flex-direction: row;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
`
const StyledUserData = styled.div`
  flex-direction: column;
  display: flex;
  flex: 1;
`
const StyledDateTime = styled.div`
  margin-left: ${convertUnit(10)};
`
const StyledMessagePreview = styled(Paragraph)`
  height: ${convertUnit(15)};
  overflow: hidden;
  text-overflow: ellipsis;
`
const StyledUsername = styled.div`
  flex-direction: row;
  display: flex;
  align-items: center;
`
const StyledAvatar = styled(Avatar)`
  aspect-ratio: 1;
  margin-right: ${convertUnit(15)};
`
const StyledIconVerifiedBadge = styled(IconVerifiedBadge)`
  display: inline-block;
  margin-right: ${convertUnit(4)};
  line-height: ${convertUnit(16)};
  margin-left: ${convertUnit(4)};
`
const StyledMessageContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  word-wrap: break-word;
  margin-top: ${convertUnit(4)};
`
const StyledChatIndicator = styled.div`
  width: ${convertUnit(16)};
  height: ${convertUnit(16)};
  min-width: ${convertUnit(16)};
  border-radius: ${convertUnit(16)};
  background-color: ${({theme}) => theme.warning_5};
`
const StyledIconSelect = styled.div`
  background-color: ${({theme}) => theme.primary_5};
  width: fit-content;
  border-radius: 100%;
  border: ${convertUnit(1)} solid ${({theme}) => theme.white_1};
  padding: ${convertUnit(0)} ${convertUnit(2)} ${convertUnit(2)};
  position: absolute;
  bottom: 0;
  right: 0;
`
const StyledAvatarContainer = styled.div`
  display: flex;
  position: relative;
  margin-right: ${convertUnit(15)};
`

export interface GiftShopMessageListItemGiftshopProps {
  members: FirebaseMessageRoomMemberItem[]
  messages: FirebaseMessageRoomItem
  roomId: string
  roomType: FirebaseMessageRoomType
  hasNewMessage: boolean
  search: string
  stateSelectedMessages: ObjectState<string[]>
  mode: MessageMode
  isSelected: boolean
}
export default function GiftShopMessageListItemGiftshop({
  members,
  messages,
  roomId,
  roomType,
  hasNewMessage,
  search,
  stateSelectedMessages,
  mode,
  isSelected,
}: GiftShopMessageListItemGiftshopProps) {
  const {id} = useSelector('user')!
  const history = useHistory()
  const participant = useMemo(() => members.find((item) => item.id !== id), [
    id,
    members,
  ])
  const {data} = useFirebaseUser({id: participant?.id || ''})
  const {photo = '', nickname = '', username = '', is_banned} = data || {}
  const {description, time} = useMessageContent({message: messages})

  const name = useMemo(
    () => (participant?.role === 'account' ? nickname : username) || '-',
    [nickname, participant?.role, username],
  )

  const isValid = useMemo(
    () => name.toLowerCase().includes(search.toLowerCase()),
    [name, search],
  )

  const handleRenderBadge = useMemo(
    () =>
      data && data.creator_status === 'verified' && <StyledIconVerifiedBadge />,
    [data],
  )

  const setSelectedMessages = stateSelectedMessages[1]

  return participant && data && isValid ? (
    <StyledContainer
      isSelected={isSelected}
      onClick={() => {
        if (mode === 'select') {
          setSelectedMessages((prev) =>
            !prev.some((x) => x === roomId)
              ? [...prev, roomId]
              : prev.filter((v) => v !== roomId),
          )
        } else {
          history.push('giftshop_message_room', {
            participantId: participant.id,
            roomType,
            roomId,
            roomName: name,
            roomPhoto: photo,
            isBanned: is_banned,
          })
        }
      }}>
      <StyledAvatarContainer>
        <StyledAvatar
          src={is_banned ? getDefaultAvatar() : photo}
          alt="Avatar"
          size={AVATAR_SIZE_CHAT}
        />
        {isSelected && (
          <StyledIconSelect>
            <Icon type="check" color="white_1" size={12} />
          </StyledIconSelect>
        )}
      </StyledAvatarContainer>
      <StyledUserData>
        <StyledHeader>
          <div>
            <StyledUsername>
              <Paragraph fontSize="m" fontWeight="bold">
                {is_banned ? 'Unavailable' : name}
              </Paragraph>
              {is_banned ? null : handleRenderBadge}
            </StyledUsername>
          </div>
          <StyledDateTime>
            <Paragraph color="gray_3">{time}</Paragraph>
          </StyledDateTime>
        </StyledHeader>
        <StyledMessageContainer>
          <StyledMessagePreview>{description}</StyledMessagePreview>
          {hasNewMessage && <StyledChatIndicator />}
        </StyledMessageContainer>
      </StyledUserData>
    </StyledContainer>
  ) : null
}
